// Frontend timecode utilities mirroring Python Timecode behavior (DF + NDF)
// Supports NTSC fractional rates 29.97, 59.94, 119.88 and integer rates.
// All math uses integer operations where possible to avoid drift.

export interface NTSCRate {
  num: number;
  den: number;
  L: number;
  drops: number;
}

export interface FpsExactResult extends NTSCRate {
  type: 'ntsc' | 'int';
}

export interface TimecodeComponents {
  hours: number;
  minutes: number;
  seconds: number;
  frames: number;
  delimiter: ':' | ';';
}

interface SplitTcResult {
  h: number;
  m: number;
  s: number;
  f: number;
  isDf: boolean;
}

interface DfParamsResult {
  L: number;
  drops: number;
  framesFirstMinute: number;
  framesPer10m: number;
  num: number;
  den: number;
}

export const NTSC_23976: NTSCRate = { num: 24000, den: 1001, L: 24, drops: 0 };
export const NTSC_2997: NTSCRate = { num: 30000, den: 1001, L: 30, drops: 2 };
export const NTSC_5994: NTSCRate = { num: 60000, den: 1001, L: 60, drops: 4 };
export const NTSC_11988: NTSCRate = { num: 120000, den: 1001, L: 120, drops: 8 };

const EPS = 1e-6;

function wrap2(n: number): string {
  const s = String(n);
  return s.length < 2 ? `0${s}` : s;
}

function fpsExact(fps: number | string): FpsExactResult {
  // Returns an object {num, den, type} where type can be 'ntsc' or 'int'.
  // If fps approximately equals 29.97/59.94/119.88, return exact NTSC rational.
  const v = Number(fps);
  if (Math.abs(v - 23.976) < EPS) return { ...NTSC_23976, type: 'ntsc' };
  if (Math.abs(v - 29.97) < EPS) return { ...NTSC_2997, type: 'ntsc' };
  if (Math.abs(v - 59.94) < EPS) return { ...NTSC_5994, type: 'ntsc' };
  if (Math.abs(v - 119.88) < EPS) return { ...NTSC_11988, type: 'ntsc' };
  // Otherwise treat as integer fps
  return { num: Math.round(v), den: 1, L: Math.round(v), drops: 0, type: 'int' };
}

function dfParams(fps: number | string, dropFrame: boolean): NTSCRate | null {
  if (!dropFrame) return null;
  const v = Number(fps);
  if (Math.abs(v - 29.97) < EPS) return NTSC_2997;
  if (Math.abs(v - 59.94) < EPS) return NTSC_5994;
  if (Math.abs(v - 119.88) < EPS) return NTSC_11988;
  throw new Error('Drop-frame only supported for 29.97, 59.94, and 119.88');
}

function splitTc(tc: string): SplitTcResult {
  // Returns {h,m,s,f,isDf}
  const isDf = tc.includes(';');
  const parts = tc.replace(';', ':').split(':');
  if (parts.length !== 4) throw new Error('Invalid timecode format');
  const [h, m, s, f] = parts.map((x) => parseInt(x, 10));
  return { h, m, s, f, isDf };
}

export class Timecode {
  fps: number | string;
  dropFrame: boolean;

  constructor(fps: number | string, dropFrame = false) {
    this.fps = fps;
    this.dropFrame = !!dropFrame;
  }

  fromFrames(n: number): string {
    // Frame index (0-based) to label
    if (n < 0) throw new Error('Frame index must be >= 0');
    return this.dropFrame ? this._fromFramesDf(n) : this._fromFramesNdf(n);
  }

  toFrames(tc: string): number {
    // Label to frame index (0-based)
    return this.dropFrame ? this._toFramesDf(tc) : this._toFramesNdf(tc);
  }

  framesToSeconds(n: number): number {
    if (n < 0) throw new Error('Frame index must be >= 0');
    const { num, den } = fpsExact(this.fps);
    // seconds = n / fps = n * den / num
    return (n * den) / num;
  }

  secondsToFrames(seconds: number): number {
    // Floors to the frame starting at or before the time
    const { num, den } = fpsExact(this.fps);
    const sec = Number(seconds);
    const total = (sec * num) / den; // fractional frames
    return Math.floor(total);
  }

  secondsToTimecode(seconds: number): string {
    if (!this.dropFrame) {
      // For NDF, derive label via exact frames-first mapping to match backend utils
      const frames = this.secondsToFrames(seconds);
      return this.fromFrames(frames);
    }

    // DF: displayed label at a real-world time instant with skip rules
    const sec = Number(seconds);
    const whole = Math.floor(sec);
    const frac = sec - whole;

    let h = Math.floor(whole / 3600);
    const rem = whole % 3600;
    let m = Math.floor(rem / 60);
    let s = rem % 60;

    const params = dfParams(this.fps, true);
    if (!params) throw new Error('Invalid DF params');
    const { L, drops } = params;
    let f = Math.floor(frac * L);
    if (m % 10 !== 0 && s === 0) {
      f += drops;
      if (f >= L) {
        f -= L;
        s += 1;
        if (s === 60) {
          s = 0;
          m += 1;
          if (m === 60) {
            m = 0;
            h += 1;
          }
        }
      }
    }
    return `${wrap2(h)}:${wrap2(m)}:${wrap2(s)};${wrap2(f)}`;
  }

  secondsToLabeledFrameIndex(seconds: number): number {
    const label = this.secondsToTimecode(seconds);
    return this.toFrames(label);
  }

  // ---------- NDF internals ----------
  _fromFramesNdf(n: number): string {
    const { L } = fpsExact(this.fps);
    const _hour = L * 60 * 60;
    const _minute = L * 60;
    const h = Math.floor(n / _hour);
    const m = Math.floor(n / _minute) % 60;
    const s = Math.floor(n / L) % 60;
    const f = n % L;
    return `${wrap2(h)}:${wrap2(m)}:${wrap2(s)}:${wrap2(f)}`;
  }

  _toFramesNdf(tc: string): number {
    const { L } = fpsExact(this.fps);
    const { h, m, s, f, isDf } = splitTc(tc);
    if (isDf) throw new Error('NDF input must use colon HH:MM:SS:FF');
    return ((h * 60 + m) * 60 + s) * L + f;
  }

  // ---------- DF internals ----------
  _dfParams(): DfParamsResult {
    const p = dfParams(this.fps, this.dropFrame);
    if (!p) throw new Error('Invalid DF params');
    const { L, drops, num, den } = p;
    // frames in first minute of 10-min block (no drop), and per 10 minutes
    const framesFirstMinute = 60 * L;
    const framesPerMinute = framesFirstMinute - drops;
    const framesPer10m = framesFirstMinute + 9 * framesPerMinute;
    return { L, drops, framesFirstMinute, framesPer10m, num, den };
  }

  _fromFramesDf(n: number): string {
    const { L, drops, framesFirstMinute, framesPer10m } = this._dfParams();

    const blocks = Math.floor(n / framesPer10m);
    const rem = n % framesPer10m;

    let minuteInBlock: number;
    let frameInMinute: number;
    if (rem < framesFirstMinute) {
      minuteInBlock = 0;
      frameInMinute = rem;
    } else {
      const remAfterFirst = rem - framesFirstMinute;
      minuteInBlock = 1 + Math.floor(remAfterFirst / (framesFirstMinute - drops));
      frameInMinute = remAfterFirst % (framesFirstMinute - drops);
    }

    const totalMinutes = blocks * 10 + minuteInBlock;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    let s: number;
    let f: number;
    if (minuteInBlock === 0) {
      s = Math.floor(frameInMinute / L);
      f = frameInMinute % L;
    } else {
      if (frameInMinute < L - drops) {
        s = 0;
        f = frameInMinute + drops;
      } else {
        const rem2 = frameInMinute - (L - drops);
        s = 1 + Math.floor(rem2 / L);
        f = rem2 % L;
      }
    }

    return `${wrap2(h)}:${wrap2(m)}:${wrap2(s)};${wrap2(f)}`;
  }

  _toFramesDf(tc: string): number {
    const { L, drops, framesFirstMinute, framesPer10m } = this._dfParams();
    const { h, m, s, f, isDf } = splitTc(tc);
    if (!isDf) throw new Error('DF input must use semicolon HH:MM:SS;FF');
    if (m % 10 !== 0 && s === 0 && f < drops) {
      throw new Error('Illegal DF label: initial dropped labels at minute not divisible by 10');
    }

    const totalMinutes = h * 60 + m;
    const blocks = Math.floor(totalMinutes / 10);
    const minuteInBlock = totalMinutes % 10;

    let framesBeforeMinute: number;
    if (minuteInBlock === 0) {
      framesBeforeMinute = 0;
    } else {
      framesBeforeMinute = framesFirstMinute + (minuteInBlock - 1) * (framesFirstMinute - drops);
    }

    let framesInSecond: number;
    if (minuteInBlock === 0) {
      framesInSecond = s * L + f;
    } else {
      if (s === 0) {
        framesInSecond = Math.max(0, f - drops);
      } else {
        framesInSecond = (s - 1) * L + f + (L - drops);
      }
    }

    return blocks * framesPer10m + framesBeforeMinute + framesInSecond;
  }
}

/**
 * Helper function to extract timecode components from a label.
 * Mirrors backend extract_timecode() behavior.
 *
 * @param label - SMPTE timecode label
 * @returns {hours, minutes, seconds, frames, delimiter}
 */
function extractTimecode(label: string): TimecodeComponents {
  const isDf = label.includes(';');
  const delimiter: ':' | ';' = isDf ? ';' : ':';
  const parts = label.replace(';', ':').split(':');
  if (parts.length !== 4) {
    throw new Error('Invalid timecode format');
  }
  const [hours, minutes, seconds, frames] = parts.map((x) => parseInt(x, 10));
  return { hours, minutes, seconds, frames, delimiter };
}

/**
 * Helper function to format timecode components into a label.
 * Mirrors backend to_timecode_format() behavior.
 *
 * @param hours
 * @param minutes
 * @param seconds
 * @param frames
 * @param delimiter - ':' for NDF, ';' for DF
 * @returns SMPTE timecode label
 */
function toTimecodeFormat(
  hours: number,
  minutes: number,
  seconds: number,
  frames: number,
  delimiter: ':' | ';',
): string {
  return `${wrap2(hours)}:${wrap2(minutes)}:${wrap2(seconds)}${delimiter}${wrap2(frames)}`;
}

interface DfAdjustmentLogContext {
  event: string;
  labelOriginal: string;
  labelAdjusted?: string;
  adjustmentType: string;
  assetInFrames: number | null;
  assetOutFrames: number | null;
  fps: number | string;
  dropFrame: boolean;
  illegalReason: string;
}

/**
 * Adjust illegal DF timecode by walking forward or backward within bounds.
 * Mirrors backend _adjust_df_illegal_with_bounds() behavior.
 *
 * @param label - Illegal DF timecode label
 * @param fps - Frame rate
 * @param dropFrame - Drop-frame flag
 * @param tc - Timecode instance
 * @param assetInFrames - Asset inpoint (0-based frame index, optional)
 * @param assetOutFrames - Asset outpoint (0-based frame index, optional)
 * @returns Adjusted frame index
 */
function _adjustDFIllegalWithBounds(
  label: string,
  fps: number | string,
  dropFrame: boolean,
  tc: Timecode,
  assetInFrames: number | null,
  assetOutFrames: number | null,
): number {
  const { hours, minutes, seconds, delimiter } = extractTimecode(label);

  // Determine drops based on fps
  let drops: number;
  const fpsVal = Number(fps);
  if (Math.abs(fpsVal - 29.97) < EPS) {
    drops = 2;
  } else if (Math.abs(fpsVal - 59.94) < EPS) {
    drops = 4;
  } else if (Math.abs(fpsVal - 119.88) < EPS) {
    drops = 8;
  } else {
    throw new Error('DF adjustment only supported for 29.97, 59.94, and 119.88');
  }

  const { L: labelsPerSecond } = fpsExact(fps);

  // Check if bounds are valid - use type narrowing
  const boundsProvided =
    assetInFrames !== null && assetInFrames !== undefined && assetOutFrames !== null && assetOutFrames !== undefined;
  const boundsValid = boundsProvided && assetInFrames <= assetOutFrames;
  // Type-narrowed bounds for use when boundsValid is true
  const inFrames = boundsValid ? assetInFrames : 0;
  const outFrames = boundsValid ? assetOutFrames : 0;

  // Try walking forward to first legal frame
  const framesForward = drops;
  const labelForward = toTimecodeFormat(hours, minutes, seconds, framesForward, delimiter);
  const forwardFrames = tc.toFrames(labelForward);

  // Check if forward adjustment stays within bounds
  if (!boundsValid || (forwardFrames >= inFrames && forwardFrames < outFrames)) {
    // Forward adjustment is valid
    if (boundsProvided && boundsValid) {
      const logContext: DfAdjustmentLogContext = {
        event: 'tc.df_illegal.adjusted_with_bounds',
        labelOriginal: label,
        labelAdjusted: labelForward,
        adjustmentType: 'forward-to-legal',
        assetInFrames,
        assetOutFrames,
        fps,
        dropFrame,
        illegalReason: 'df_minute_start',
      };
      console.warn('DF illegal timecode adjusted with bounds', logContext);
    }
    return forwardFrames;
  }

  // Forward exceeds outpoint, try walking backward
  let secondsBackward = seconds - 1;
  let minutesBackward = minutes;
  let hoursBackward = hours;

  if (secondsBackward < 0) {
    secondsBackward = 59;
    minutesBackward -= 1;
    if (minutesBackward < 0) {
      minutesBackward = 59;
      hoursBackward -= 1;
      if (hoursBackward < 0) {
        // Cannot walk backward (would be negative time), clamp to inpoint
        if (boundsProvided && boundsValid) {
          const logContext: DfAdjustmentLogContext = {
            event: 'tc.df_illegal.adjusted_with_bounds',
            labelOriginal: label,
            adjustmentType: 'clamped-to-inpoint',
            assetInFrames,
            assetOutFrames,
            fps,
            dropFrame,
            illegalReason: 'df_minute_start',
          };
          console.warn('DF illegal timecode clamped to inpoint', logContext);
        }
        return inFrames;
      }
    }
  }

  const framesBackward = labelsPerSecond - 1;
  const labelBackward = toTimecodeFormat(hoursBackward, minutesBackward, secondsBackward, framesBackward, delimiter);
  const backwardFrames = tc.toFrames(labelBackward);

  // Check if backward adjustment stays within bounds
  if (boundsValid && backwardFrames >= inFrames && backwardFrames < outFrames) {
    const logContext: DfAdjustmentLogContext = {
      event: 'tc.df_illegal.adjusted_with_bounds',
      labelOriginal: label,
      labelAdjusted: labelBackward,
      adjustmentType: 'backward-to-legal',
      assetInFrames,
      assetOutFrames,
      fps,
      dropFrame,
      illegalReason: 'df_minute_start',
    };
    console.warn('DF illegal timecode adjusted with bounds', logContext);
    return backwardFrames;
  }

  // Neither forward nor backward works, clamp to nearest boundary
  if (boundsValid) {
    if (forwardFrames >= outFrames) {
      // Forward exceeded, clamp to last valid frame (outpoint - 1 since outpoint is exclusive)
      const clampedFrame = outFrames - 1;
      const logContext: DfAdjustmentLogContext = {
        event: 'tc.df_illegal.adjusted_with_bounds',
        labelOriginal: label,
        adjustmentType: 'clamped-to-outpoint',
        assetInFrames,
        assetOutFrames,
        fps,
        dropFrame,
        illegalReason: 'df_minute_start',
      };
      console.warn('DF illegal timecode clamped to outpoint', logContext);
      return clampedFrame;
    } else {
      // Backward was below inpoint, clamp to inpoint
      const logContext: DfAdjustmentLogContext = {
        event: 'tc.df_illegal.adjusted_with_bounds',
        labelOriginal: label,
        adjustmentType: 'clamped-to-inpoint',
        assetInFrames,
        assetOutFrames,
        fps,
        dropFrame,
        illegalReason: 'df_minute_start',
      };
      console.warn('DF illegal timecode clamped to inpoint', logContext);
      return inFrames;
    }
  }

  // No bounds, just return forward adjustment
  return forwardFrames;
}

/**
 * Parse a SMPTE label to frames with bounds-aware DF illegal adjustment.
 *
 * Mirrors backend behavior:
 * - Try strict parse with the given fps/dropframe.
 * - If it raises DF minute-start illegality, apply bounds-aware adjustment:
 *   - Try walking forward to first legal frame (e.g., ;01 -> ;02)
 *   - If forward exceeds assetOutFrames, try walking backward instead
 *   - If neither works or no bounds provided, use forward adjustment
 * - Otherwise, re-raise the original error.
 *
 * Note: 59.94i->29.97 framerate treatment happens before this function is called,
 * so by the time we get here, 59.94i assets are already treated as 29.97.
 *
 * @param label - SMPTE timecode label
 * @param fps - Frame rate (already converted from 59.94i to 29.97 if applicable)
 * @param dropFrame - Drop-frame flag
 * @param assetInFrames - Asset inpoint (0-based frame index, optional)
 * @param assetOutFrames - Asset outpoint (0-based frame index, optional)
 * @returns Frame index (0-based)
 */
export function toFramesWithFallback(
  label: string,
  fps: number | string,
  dropFrame: boolean,
  assetInFrames: number | null = null,
  assetOutFrames: number | null = null,
): number {
  const tc = new Timecode(fps, dropFrame);
  try {
    return tc.toFrames(label);
  } catch (err) {
    // Only handle DF minute-start illegality
    const isMinuteStartError = err instanceof Error && err.message && err.message.includes('Illegal DF label');

    if (dropFrame && isMinuteStartError) {
      // Apply bounds-aware adjustment for all DF rates
      try {
        return _adjustDFIllegalWithBounds(label, fps, dropFrame, tc, assetInFrames, assetOutFrames);
      } catch {
        // Fall through to propagate the original error
      }
    }
    // Propagate original error
    throw err;
  }
}
